/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import { Room, RoomFormValues, RoomType } from "../../interfaces/RoomInterface";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import { getRooms, postRoom, patchRoom, deleteRoom } from "../../api/room";
import { TWardType } from "../../interfaces/WardInterface";

export const roomsQueryKey = "rooms";

// use rooms
export const useRooms = (options?: {
  wardIds?: Array<number>;
  type?: RoomType;
  wardType?: TWardType;
}): UseQueryResult<Array<Room>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [
      roomsQueryKey,
      resourceCentre?.id,
      options?.wardIds,
      options?.type,
      options?.wardType
    ],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getRooms({
        resourceCentreId: Number(resourceCentre.id),
        ...options
      });

      return response.data || [];
    },
    enabled: !!resourceCentre?.id
  });
};

// use post room
export const usePostRoom = (): UseMutationResult<
  ApiResponse<Room>,
  Error,
  RoomFormValues,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: (data: RoomFormValues) =>
      postRoom({ ...data, resourceCentreId: Number(resourceCentre?.id) }),

    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [roomsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: response?.message || "Room created successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create room",
          variant: "error"
        })
      );
    }
  });
};

// use patch room
export const usePatchRoom = (): UseMutationResult<
  ApiResponse<Room>,
  Error,
  { id: number | string; data: RoomFormValues },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: ({ id, data }) => patchRoom({ id, data }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [roomsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: response?.message || "Room updated successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to update room",
          variant: "error"
        })
      );
    }
  });
};

// use delete room
export const useDeleteRoom = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: (id) => deleteRoom(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [roomsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: response?.message || "Room deleted successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to delete room",
          variant: "error"
        })
      );
    }
  });
};
